export type Facing = 'user' | 'environment';

export class CameraController {
  private videoEl: HTMLVideoElement;
  private stream: MediaStream | null = null;
  private facing: Facing = 'environment';

  constructor(videoEl: HTMLVideoElement) {
    this.videoEl = videoEl;
  }

  get currentFacing(): Facing {
    return this.facing;
  }

  stop() {
    this.stream?.getTracks().forEach(t => t.stop());
    this.stream = null;
  }

  async toggle() {
    const next: Facing = this.facing === 'user' ? 'environment' : 'user';
    await this.start(next);
  }

  async start(facing: Facing) {
    this.stop();

    try {
      const constraints = this.buildExactConstraints(facing);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      await this.attach(stream, facing);
      return;
    } catch (err) {
      console.warn(`[CameraController] Falhou facingMode exato (${facing}). Erro:`, err);
    }

    try {
      const tmp = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      tmp.getTracks().forEach(t => t.stop());
    } catch (err) {
      console.warn("[CameraController] Falhou ao solicitar permissão genérica:", err);
    }

    const fallback = await this.buildDeviceIdFallback(facing);
    const stream = await navigator.mediaDevices.getUserMedia(fallback);
    await this.attach(stream, facing);
  }

  private async attach(stream: MediaStream, facing: Facing) {
    this.stream = stream;
    this.facing = facing;
    this.videoEl.srcObject = stream;
    this.videoEl.setAttribute('playsinline', 'true');
    try { 
      await this.videoEl.play(); 
    } catch (err) { 
      console.warn("[CameraController] Erro ao reproduzir vídeo:", err);
    }
  }

  private buildExactConstraints(facing: Facing): MediaStreamConstraints {
    const video: MediaTrackConstraints = {
      facingMode: { exact: facing },
      width: { ideal: 1280 },
      height: { ideal: 720 },
    };
    return { video, audio: false };
  }

  private async buildDeviceIdFallback(facing: Facing): Promise<MediaStreamConstraints> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const inputs = devices.filter(d => d.kind === 'videoinput');

    const matcher =
      facing === 'user'
        ? /front|frontal|selfie|user/i
        : /back|traseira|rear|environment/i;

    const byLabel = inputs.find(d => matcher.test(d.label));

    const deviceId =
      byLabel?.deviceId ??
      (facing === 'environment' && inputs[1]?.deviceId) ??
      inputs[0]?.deviceId;

    if (deviceId) {
      return { video: { deviceId: { exact: deviceId } }, audio: false };
    }

    return { video: true, audio: false };
  }
}